import React from "react";
import ModalHeader from "@/customComponents/uiComponents/modal/ModalHeader.jsx";
import {ModalFooter} from "../Components/BookingModalShell.jsx";
import {flightService} from "@/services/flightService";
import {BOOKING_CONSTRAINTS} from "@/constants/booking.js";
import {tripNow, formatTripLocalTime, formatDHM} from "@/utils/dateUtils.js";
import {storage, STORAGE_KEYS} from "@/utils/storage";
import {MODAL_STEPS} from "@/constants/steps";
import {formatBufferApprox} from "@/utils/policyFormat";
import {isNearAirport, getWaitingBucket, getAirportBufferMinutes} from "@/utils/airports";
import {waitingItemImage} from "@/customComponents/sharedComponents/smartTime/waitingIcons";
import {
    boldCar,
    shieldProtected,
    infoIcon,
    pointAddedIcon,
    arrRight,
} from "@img/index.js";

export function RecommendedPickupDetails(props) {
    const flightDetails = storage.get(STORAGE_KEYS.SMART_TIME_FLIGHT_DETAILS, null);
    const isEditMode = storage.get(STORAGE_KEYS.EDIT_STEP) === true;

    // fromAirport screen: the cushion and the rows both come from the PICKUP airport's
    // own waiting_times bucket (/config/airports), picked by flight scope. Falls back to
    // the global booking.from_airport_extra_minutes when the bucket is unconfigured.
    const pickupDest = (storage.get(STORAGE_KEYS.DESTINATIONS, []) || [])[0];
    const pickupAirport = pickupDest?.lat && pickupDest?.lng ? isNearAirport(pickupDest.lat, pickupDest.lng) : null;
    const waitingOpts = {fromAirport: true, international: flightService.getFlightType(flightDetails) !== 'Domestic'};
    const waitingItems = getWaitingBucket(pickupAirport, waitingOpts)?.items ?? [];
    const AIRPORT_BUFFER_MINUTES = getAirportBufferMinutes(pickupAirport, waitingOpts);

    const calc = (() => {
        if (!flightDetails?.arrival?.scheduledTime?.local) return null;
        const flightArrivalLocal = flightService.parseLocalTime(flightDetails.arrival.scheduledTime.local);
        const recommendedTime = new Date(flightArrivalLocal.getTime() + AIRPORT_BUFFER_MINUTES * 60000);
        const mins = recommendedTime.getMinutes();
        const remainder = mins % 5;
        if (remainder !== 0) recommendedTime.setMinutes(mins + (5 - remainder), 0, 0);
        else recommendedTime.setSeconds(0, 0);
        return {recommendedTime};
    })();

    const formatTime = (date) =>
        date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit'});
    const formatDateLabel = (date) =>
        date.toLocaleDateString('en-US', {weekday: 'short', month: 'short', day: 'numeric'});
    const confirmDateTimeStr = calc
        ? `${formatTime(calc.recommendedTime)}, ${formatDateLabel(calc.recommendedTime)}`
        : '';

    const getTimeError = () => {
        if (!calc) return false;
        const now = tripNow();
        const minLeadMs = BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES * 60 * 1000;
        const minAllowed = new Date(now.getTime() + minLeadMs);
        if (calc.recommendedTime.getTime() <= now.getTime()) {
            return "The recommended pickup time has already passed for this flight.";
        }
        if (calc.recommendedTime.getTime() < minAllowed.getTime()) {
            const contactNumber = BOOKING_CONSTRAINTS.CONTACT_NUMBER;
            const mins = BOOKING_CONSTRAINTS.MINIMUM_BOOKING_LEAD_TIME_MINUTES;
            const leadTimeStr = formatDHM(mins);
            const earliestMins = minAllowed.getMinutes();
            const remainder = earliestMins % 5;
            if (remainder !== 0) minAllowed.setMinutes(earliestMins + (5 - remainder), 0, 0);
            const earliestTime = formatTripLocalTime(minAllowed);
            return `For quick bookings, please contact ${contactNumber}. Online booking is only available ${leadTimeStr} in advance (earliest available: ${earliestTime}).`;
        }
        return false;
    };
    const timeError = getTimeError();

    const handleConfirm = () => {
        if (!calc) return;
        storage.set(STORAGE_KEYS.SMART_TIME_SELECTED, '1');
        storage.set(STORAGE_KEYS.RIDE_TIME, calc.recommendedTime.toString());
        props.setStep(isEditMode ? MODAL_STEPS.ORDER_DETAILS : MODAL_STEPS.SELECTED_CAR_DETAILS);
    };

    return (
        <>
            <ModalHeader
                title=""
                extraClasses=""
                totalSteps={0}
                cancelButton={false}
                cancelButtonType="icon"
                backStep={MODAL_STEPS.SMART_TIME_SELECT}
                showBackButton={true}
                setStep={props.setStep}
                closeModal={props.closeModal}
            />

            <div className="modalPages-spacing recommendedPickupDetails-step">
                <div className="hopn-pickup-options to-airport">
                    <div className="hopn-pickup-options-grid">
                        <div className="hopn-pickup-options-grid-col">
                            <div className="hopn-pickup-option recommended selected expanded">
                                <div className="recommendation-tabs">
                                    <div className="recommendation-list">
                                        <span>
                                            <img src={boldCar} alt=""/>
                                        </span>
                                        <div className="recommendation-content">
                                            <div className="recommendation-content-list">
                                                <h3>Recommended pickup {calc && <span>{formatTime(calc.recommendedTime)}</span>}</h3>
                                            </div>
                                            <div className="recommendation-content-data">
                                                <h6>No waiting charges</h6>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="recommendation-content-data">
                                        <div className="hopn-why-pickup-time">
                                            <div className="hopn-why-pickup-time-icon">
                                                <img src={shieldProtected} alt=""/>
                                            </div>
                                            <div className="hopn-why-pickup-time-text">
                                                <h6>Why this pickup time?</h6>
                                                <p>Your driver monitors your flight and adjusts pickup time automatically</p>
                                            </div>
                                        </div>
                                        {/* The airport's own waiting_times items (/config/airports). */}
                                        <ul className="hopn-pickup-breakdown-list">
                                            {waitingItems.map((item, i) => (
                                                <li key={i}>
                                                    <span className="hopn-breakdown-icon">
                                                        <img src={waitingItemImage(item.name)} alt=""/>
                                                    </span>
                                                    <span className="hopn-breakdown-label">{item.name}</span>
                                                    <span className="hopn-breakdown-value">~{formatBufferApprox(item.minutes)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <p className="hopn-pickup-total">
                                            <span className="hopn-pickup-total-label"><strong>Total</strong> estimated time at airport:</span>
                                            <span className="hopn-pickup-total-value">
                                                ~{formatBufferApprox(AIRPORT_BUFFER_MINUTES)} before departure
                                                <button type="button" className="hopn-info-icon" onClick={() => props.setStep(MODAL_STEPS.LIABILITY_DISCLAIMER)}>
                                                    <img src={infoIcon} alt="Liability disclaimer"/>
                                                </button>
                                            </span>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {timeError && (
                    <div className="hopn-smart-time-date-warning error">
                        <img decoding="async" src={pointAddedIcon} alt="Warning"/>
                        <span>{timeError}</span>
                    </div>
                )}
            </div>

            <ModalFooter className="recommendedPickupDetails-actions">
                <button
                    type="button"
                    className="hopn-add-passenger-button duble-title"
                    disabled={!!timeError || !calc}
                    onClick={handleConfirm}
                >
                    <div className="multiple-text">
                        <span>Confirm pickup time</span>
                        <span>{confirmDateTimeStr}</span>
                    </div>
                </button>

                <button
                    type="button"
                    className="hopn-choose-own-time-link"
                    onClick={() => {
                        storage.set(STORAGE_KEYS.TDT_SHOW_WAITING_INFO, true);
                        storage.set(STORAGE_KEYS.SMART_TIME_SECTION, 'custom');
                        props.setStep(MODAL_STEPS.TRAVELING_DATE_TIME);
                    }}
                >
                    I'll choose my own time
                    <img src={arrRight} alt=""/>
                </button>
            </ModalFooter>
        </>
    );
}

export default RecommendedPickupDetails;
